/**
 * Centralized activity event type configuration
 *
 * Maps timeline event types to icons and i18n translation keys.
 * Mirrors the notificationTypes.ts pattern for consistency.
 *
 * Icons are PrimeReact icons (https://primereact.org/icons)
 */

import { TimelineEventType } from 'types/timeline';

/**
 * Configuration for a single activity event type
 */
export interface ActivityEventTypeConfig {
  value: TimelineEventType;
  labelKey: string;
  icon: string;
}

/**
 * All supported timeline event types with display metadata
 */
/**
 * Every `LedgerEntryType`, with display metadata.
 *
 * Rebuilt on the ledger vocabulary (EAINT-764 Stage 2 / OQ-3). The previous list keyed on the
 * legacy PascalCase `TimelineEventType` values, which the API stopped sending when the timeline
 * moved onto `ledger_entries` — so `getActivityEventTypeConfig` matched nothing and every event
 * rendered with the fallback icon and an untranslated label.
 *
 * It also declared several types no writer ever produced (ArcActivated / ArcCompleted /
 * ArcCancelled / ArcFailed, RoleMemberAdded/Removed, FactorModeChanged, AttachmentUpdated), which
 * made the filter UI offer choices that could never match an event.
 *
 * Icons carry over from the equivalent legacy concept wherever one existed, so the visual language
 * is unchanged for events users already recognise.
 */
export const ACTIVITY_EVENT_TYPE_CONFIGS: ActivityEventTypeConfig[] = [
  {
    value: 'sphere_created',
    labelKey: 'activityFeed.events.sphereCreated',
    icon: 'pi-plus-circle',
  },
  {
    value: 'sphere_property_changed',
    labelKey: 'activityFeed.events.propertyChanged.generic',
    icon: 'pi-pencil',
  },
  {
    value: 'permission_changed',
    labelKey: 'activityFeed.events.permissionChanged',
    icon: 'pi-shield',
  },
  {
    value: 'arc_transition',
    labelKey: 'activityFeed.events.arcTransition',
    icon: 'pi-directions',
  },
  {
    value: 'arc_lifecycle',
    labelKey: 'activityFeed.events.arcLifecycle',
    icon: 'pi-sitemap',
  },
  {
    value: 'stage_action',
    labelKey: 'activityFeed.events.stageAction',
    icon: 'pi-bolt',
  },
  {
    value: 'check_in',
    labelKey: 'activityFeed.events.checkIn',
    icon: 'pi-check-square',
  },
  {
    value: 'task_completed',
    labelKey: 'activityFeed.events.taskCompleted',
    icon: 'pi-check-circle',
  },
  {
    value: 'factor_lifecycle',
    labelKey: 'activityFeed.events.factorLifecycle',
    icon: 'pi-sliders-h',
  },
  {
    value: 'perspective_assigned',
    labelKey: 'activityFeed.events.perspectiveAssigned',
    icon: 'pi-user-plus',
  },
  {
    value: 'perspective_unassigned',
    labelKey: 'activityFeed.events.perspectiveUnassigned',
    icon: 'pi-user-minus',
  },
  {
    value: 'aspect_observation',
    labelKey: 'activityFeed.events.aspectObservation',
    icon: 'pi-eye',
  },
  {
    value: 'aspect_validation',
    labelKey: 'activityFeed.events.aspectValidation',
    icon: 'pi-verified',
  },
  {
    value: 'aspect_attached',
    labelKey: 'activityFeed.events.aspectAttached',
    icon: 'pi-link',
  },
  {
    value: 'aspect_detached',
    labelKey: 'activityFeed.events.aspectDetached',
    icon: 'pi-times-circle',
  },
  {
    value: 'attachment_added',
    labelKey: 'activityFeed.events.attachmentAdded',
    icon: 'pi-paperclip',
  },
  {
    value: 'attachment_removed',
    labelKey: 'activityFeed.events.attachmentRemoved',
    icon: 'pi-trash',
  },
  {
    value: 'status_propagated',
    labelKey: 'activityFeed.events.statusPropagated',
    icon: 'pi-share-alt',
  },
  {
    value: 'velocity_changed',
    labelKey: 'activityFeed.events.velocityChanged.label',
    icon: 'pi-forward',
  },
  {
    value: 'impact_changed',
    labelKey: 'activityFeed.events.impactChanged.label',
    icon: 'pi-chart-line',
  },
];

/**
 * Lookup map for O(1) access by event type
 */
const CONFIG_MAP = new Map<string, ActivityEventTypeConfig>(
  ACTIVITY_EVENT_TYPE_CONFIGS.map((c) => [c.value, c])
);

/**
 * Default config for unknown event types
 */
const DEFAULT_CONFIG: ActivityEventTypeConfig = {
  value: 'SphereCreated' as TimelineEventType,
  labelKey: 'activityFeed.events.unknown',
  icon: 'pi-info-circle',
};

/**
 * Get config for a specific event type, returns default for unknown types
 */
export function getActivityEventTypeConfig(eventType: string): ActivityEventTypeConfig {
  return CONFIG_MAP.get(eventType) ?? DEFAULT_CONFIG;
}
